import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';

import moment from 'moment';
import { Observable, finalize } from 'rxjs';

import { Milestone, Product, Project, Purchased } from '../schemas';
import { AgreementService, Mail, Messenger, ProductService } from '../service';

export type InvoiceFormData = {
  ownerId: string;
  projects: Project[];
  hoursType?: string;
} & ({ item: Milestone } | { item: Purchased; ownerType: 'product' | 'agreement' });

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSelectModule,
    MatInputModule,
    FormField,
  ],
  template: `
    <h2 mat-dialog-title>Invoice</h2>

    <mat-dialog-content>
      <div class="entity-form">
        <mat-form-field>
          <mat-label> Status </mat-label>
          <mat-select [formField]="form.Status">
            @for (item of statuses; track item[0]) {
              <mat-option [value]="item[0]">{{ item[1] }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (form.Status().value() === 20) {
          <mat-form-field>
            <mat-label> Notes </mat-label>
            <textarea matInput [formField]="form.Notes"></textarea>
          </mat-form-field>
        } @else if (form.Status().value() === 10) {
          <mat-form-field>
            <mat-label> Message </mat-label>
            <textarea matInput [formField]="form.message"></textarea>
          </mat-form-field>
        }
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button matMiniFab matTooltip="Save client details" (click)="save()" [disabled]="!form().valid() || pending()">
        <mat-icon>save</mat-icon>
      </button>
    </mat-dialog-actions>
    @if (pending()) {
      <mat-progress-bar mode="indeterminate" />
    }
  `,
})
export class InvoiceForm {
  data = inject(MAT_DIALOG_DATA) as InvoiceFormData;
  hostDialog = inject(MatDialogRef);
  messenger = inject(Messenger);
  mailer = inject(Mail);
  productService = inject(ProductService);
  agreementService = inject(AgreementService);

  statuses = [
    [0, 'Not ready to invoice'],
    [10, 'Ready to invoice'],
    [20, 'Invoiced'],
  ];

  pending = signal(false);

  model = signal({
    message: this.mailer.getReadyToInvoiceMsg(),
    ...this.data.item,
  });
  form = form(this.model);

  save() {
    const { message, ...data } = this.form().value();
    this.pending.set(true);

    const ownerType = this.data['ownerType'] ?? 'product';

    let save$: Observable<Record<string, never> | Product>;

    if (this.data.hoursType) {
      const service = ownerType === 'agreement' ? this.agreementService : this.productService;
      save$ = service.updateHours(this.data.ownerId, data, this.data.hoursType);
    } else {
      save$ = this.productService.updateMilestone(this.data.ownerId, data);
    }

    data.InvoiceDate = moment().format('YYYY-MM-DD');
    save$.pipe(finalize(() => this.pending.set(false))).subscribe({
      next: () => {
        this.hostDialog.close(true);
        // Send email notification only when marking as 'Ready to invoice' (status 10)
        if (data.Status === 10) {
          const project = this.data.projects.find(({ Id }) => Id === data.ProjectId);
          if (project) {
            this.mailer.sendPendingInvoice(message, project, ownerType, this.data.ownerId);
          }
        }
      },
      error: this.messenger.handleHttpError,
    });
  }
}
